import { useCallback, useState } from "react"
import { GetApp } from "../../App"
import { logger } from "../../App/Logger"
import { apiErrorReason } from "../../App/ApiError"
import { useEditState } from "../../App/useEditState"
import { useDataStore } from "../../Data"
import { IAccountUserLevel } from "../../Types"
import { createStoreAccountUserWriters } from "../AccountSettings/AccountUserWriters"
import { UseAccountUserEditOptions } from "./Params"

// Shared hook for the AccountUser row: the level-select auto-save (via EditGroup's
// onSave) plus the direct deactivate/reactivate/remove menu actions. All four paths
// share one `error` slot (only one action is ever in flight per row) fed by the
// server's `accountUserSelfAction`/`accountUserLastAdmin` guards -- surfaced through
// EditGroup's own `error` prop the same way NMEAConfiguration/ConnectionView does.

const fallbackError = "account_user_action_failed";

export type AccountUserPendingAction = "deactivate" | "remove" | undefined;

export function useAccountUserEdit({ userInfo, onChanged, initError }: UseAccountUserEditOptions) {
  const app = GetApp();
  const store = useDataStore();
  const t = app.getT("AccountSettingsView");
  const [edited, onEdited, resetEdited] = useEditState();
  const [error, setError] = useState<string | undefined>(initError);
  const [pendingAction, setPendingAction] = useState<AccountUserPendingAction>(undefined);
  const writers = createStoreAccountUserWriters(store, {
    accountId: app.account?.id ?? app.user?.accountId ?? missingAccountId(),
  });

  const applyLevel = useCallback(async (accountLevel: IAccountUserLevel) => {
    logger.debug("updateAccountUserLevel", userInfo.user.id, accountLevel);
    setError(undefined);
    try {
      await writers.updateAccountUserLevel(userInfo.user.id, accountLevel);
      onChanged();
    } catch (err) {
      // Named or not, the failure has to reach the row (#1820): the old guard tested a
      // wire shape the server stopped sending, so a rejected action showed nothing.
      setError(apiErrorReason(err) || fallbackError);
    } finally {
      // EditGroup auto-resubmits for as long as `changed` stays true (see its useEffect),
      // so this must run on failure too -- otherwise a rejected select-driven save retries
      // forever.
      resetEdited();
    }
  }, [userInfo, onChanged]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  const updateAccountUser = useCallback(async (_data: FormData, values: any) => {
    logger.debug("updateAccountUser", userInfo.user.id);
    await applyLevel(values.accountLevel);
  }, [applyLevel]);

  const remove = useCallback(async () => {
    logger.debug("removeAccountUser", userInfo.user.id);
    setError(undefined);
    try {
      await writers.removeAccountUser(userInfo.user.id);
      onChanged();
    } catch (err) {
      // Named or not, the failure has to reach the row (#1820): the old guard tested a
      // wire shape the server stopped sending, so a rejected action showed nothing.
      setError(apiErrorReason(err) || fallbackError);
    }
  }, [userInfo, onChanged]);

  // #1025 (tracker #827): deactivating/removing a member who still has ship access somewhere in the
  // account (the same zero-remaining-ship-access condition the server's AccountUser
  // cascade itself uses -- see `userInfo.hasShipAccess`) warns first via a
  // ConfirmDialog (#200); a member without ship access is unaffected by the cascade,
  // so those actions still fire immediately, matching #979's existing behavior.
  const deactivate = useCallback(() => {
    if (userInfo.hasShipAccess) {
      setPendingAction("deactivate");
    } else {
      applyLevel(IAccountUserLevel.disabled);
    }
  }, [applyLevel, userInfo]);
  const reactivate = useCallback(() => applyLevel(IAccountUserLevel.user), [applyLevel]);
  const requestRemove = useCallback(() => {
    if (userInfo.hasShipAccess) {
      setPendingAction("remove");
    } else {
      remove();
    }
  }, [remove, userInfo]);

  const cancelPendingAction = useCallback(() => setPendingAction(undefined), []);
  const confirmPendingAction = useCallback(async () => {
    if (pendingAction === "deactivate") {
      await applyLevel(IAccountUserLevel.disabled);
    } else if (pendingAction === "remove") {
      await remove();
    }
    setPendingAction(undefined);
  }, [pendingAction, applyLevel, remove]);

  return { app, t, edited, onEdited, error, updateAccountUser, deactivate, reactivate, remove: requestRemove, pendingAction, cancelPendingAction, confirmPendingAction };
}

function missingAccountId(): never {
  throw new Error("AccountUser mutation requires an active account");
}
